import Decimal from 'decimal.js';
import Numeric from '@/core/types/Numeric';
import Qty     from '@/core/types/Qty';
import Price   from '@/core/types/Price';
import MoneyUtils from '@/core/utils/MoneyUtils';
import NumberUtils from '@/core/utils/NumberUtils';
import type Currency from '@/domains/Currencies/Currency';
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import CurrencyRate from "@/core/types/CurrencyRate";
import Multiplier from "@/core/types/Multiplier";
import Core from "@/core/Core";
import type DateSimple from "@/core/types/DateSimple";
import type VatRate from "@/core/types/VatRate";

@Title_ru_RU_Class('Сумма')
class Sum extends Numeric {
    static readonly SCALE = 2;

    static fromServer(value: string): Sum {
        return new Sum(value);
    }

    /** Результат вычисления — уже посчитанный Decimal. */
    static fromOperation(value: Decimal): Sum {
        return new Sum(value);
    }

    /** Сумма + сумма = сумма. */
    plus(other: Sum | null | undefined): Sum {
        return Sum.fromOperation(this.value.plus(Sum.toDecimal(other)));
    }

    /** Сумма − сумма = сумма. */
    minus(other: Sum | null | undefined): Sum {
        return Sum.fromOperation(this.value.minus(Sum.toDecimal(other)));
    }

    /**
     * Сумма × курс = сумма (в другой валюте).
     * Мультивалютный документ: сумма_в_валюте × курс = локальная сумма.
     *
     * Сумма × множитель = сумма (наценка: 1.20 даёт +20%).
     *
     * Реализация одна на оба случая: и курс, и множитель — безразмерные
     * коэффициенты, результат в обоих случаях остаётся суммой. Сигнатуры
     * разные, чтобы на месте вызова было видно, что именно применяют.
     */
    mul(rate: CurrencyRate | null | undefined): Sum;
    mul(multiplier: Multiplier | null | undefined): Sum;
    mul(operand: CurrencyRate | Multiplier | null | undefined): Sum {
        return Sum.fromOperation(this.value.times(Sum.toDecimal(operand)));
    }

    /**
     * Полная сумма (с НДС). Логика — в MoneyUtils.getFull (одна на фронт),
     * здесь только оборачиваем результат в Sum.
     */
    getFull(isVatIncluded: boolean | null, vatRate: VatRate | null | undefined): Sum {
        return Sum.fromOperation(MoneyUtils.getFull(this.value, isVatIncluded, vatRate?.value));
    }

    /**
     * Сумма без НДС — операция, обратная getFull. Логика — в MoneyUtils.getNet
     * (одна на фронт), здесь только оборачиваем результат в Sum.
     *
     * Ставка не задана — снимать нечего, сумма возвращается как есть. То же
     * правило, что в getFull: расчёт дёргается на каждое изменение формы,
     * включая незаполненную.
     */
    getNet(isVatIncluded: boolean | null, vatRate: VatRate | null | undefined): Sum {
        if (vatRate === null || vatRate === undefined) {
            return this;
        } else {
            return Sum.fromOperation(MoneyUtils.getNet(this.value, isVatIncluded === true, vatRate.value));
        }
    }

    /**
     * Сумма / цена = количество.
     *
     * Обратная к Price.mul(Qty) и зеркало div(Qty): там сумма делится на
     * количество и даёт цену, здесь — на цену и даёт количество. Нужна там,
     * где ведущая величина документа деньги, а количество производно:
     * в платеже сумма известна из счёта, а количество выводится из неё.
     *
     * Деление почти всегда даёт бесконечный хвост — результат округляется до
     * scale количества (3), поэтому обратной сходимости количество × цена =
     * сумма в общем случае нет.
     */
    divByPrice(price: Price | null | undefined): Qty {
        return Qty.fromOperation(this.value.dividedBy(Sum.toDecimal(price)));
    }

    /**
     * НДС из этой суммы. Логика — в MoneyUtils.getVat (одна на фронт),
     * здесь только оборачиваем результат в Sum.
     */
    getVat(isVatIncluded: boolean | null, vatRate: VatRate | null | undefined): Sum {
        return Sum.fromOperation(MoneyUtils.getVat(this.value, isVatIncluded, vatRate?.value));
    }

    /**
     * Сумма / количество = цена.
     * Мультивалютный документ читается «справа налево»: сумма первична,
     * цена производна. Деление почти всегда даёт бесконечный хвост —
     * результат округляется до scale цены (3). Обратной сходимости
     * цена × количество = сумма в общем случае НЕТ — это свойство УПД,
     * а не дефект расчёта.
     */
    div(qty: Qty | null | undefined): Price;
    div(multiplier: Multiplier): Sum;
    div(operand: Qty | Multiplier | null | undefined): Price | Sum {
        /** Тип результата задаёт операнд, но у пустого операнда типа нет.
         *  Трактуем пустоту как количество: сумма / количество = цена —
         *  это ходовой путь (позиция с очищенным количеством), а снятие
         *  наценки пустым множителем смысла не имеет. */
        if (operand instanceof Qty || operand === null || operand === undefined) {
            // decimal.js div округляет по precision (20 значащих), не усекает.
            // Зеркало на беке: Number::div, затем round до 3 в конструкторе Price.
            return Price.fromOperation(this.value.dividedBy(Sum.toDecimal(operand)));
        } else {
            // Снятие наценки: сумма остаётся суммой.
            return Sum.fromOperation(this.value.dividedBy(operand.value));
        }
    }

    /**
     * Человекочитаемое представление суммы: число + символ валюты.
     * У суммы нет единицы измерения (в отличие от Price) — только валюта.
     * Ядро форматирования числа — общий NumberUtils.formatDecimal; символ
     * валюты клеится постфиксом неразрывным пробелом (\u00A0), чтобы число и
     * символ не рвались по разным строкам.
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     *
     * @param currency - валюта: объект или код (символ клеится постфиксом; null — без валюты)
     */
    format(currency: Currency | string | null = null): string {
        if (this.isEmpty()) {
            return '';
        }

        let result = NumberUtils.formatDecimal(this.value, this.scale);
        /** Валюта принимается и объектом, и кодом — в моделях она лежит
         *  по-разному. Неизвестный код даст null: печатаем число без символа,
         *  само значение от этого верным быть не перестаёт. */
        const currencyObject = MoneyUtils.resolveCurrency(currency);

        if (currencyObject) {
            result += `\u00A0${currencyObject.symbol}`;
        }

        return result;
    }

    /**
     * Сокращённая сумма с валютой: "12,5 тыс ₽", "3,45 млн сўм".
     * Символ валюты клеится постфиксом неразрывным пробелом — как в format.
     * Пустое значение печатается пустой строкой: валюта без числа смысла не имеет.
     *
     * @param currency - валюта: объект или код (null — без валюты)
     */
    formatShorten(currency: Currency | string | null = null): string {
        if (this.isEmpty()) {
            return '';
        }

        let result = super.formatShorten();
        const currencyObject = MoneyUtils.resolveCurrency(currency);

        if (currencyObject !== null) {
            result += `\u00A0${currencyObject.symbol}`;
        }

        return result;
    }

    /**
     * Сумма / сумма = множитель (отношение двух сумм).
     * Например: сумма реализации / сумма отгрузки-основания = коэффициент наценки.
     */
    divBySum(other: Sum | null | undefined): Multiplier {
        return Multiplier.fromOperation(this.value.dividedBy(Sum.toDecimal(other)));
    }

    /**
     * Сумма, приведённая к другой валюте. Логика — в MoneyUtils.convertSync
     * (одна на фронт), здесь только приведение валют к объектам.
     *
     * Сам Sum валюту не несёт — исходная приходит параметром, наравне с
     * целевой. Обе принимаются и объектом, и кодом: в моделях валюта лежит
     * по-разному.
     *
     * Sync в имени — курс берётся из реактивного кеша, сервер не ждётся.
     * Осмысленный результат метод даёт лишь внутри реактивного контекста
     * (шаблон, computed): присвоенный в обычное поле, он не пересчитается
     * ни при появлении курса, ни при смене валюты.
     *
     * null — валюта неизвестна, то есть дефект данных. Пустая сумма здесь не
     * годится: снаружи она неотличима от «не введено» и от «курса нет», а
     * попав в plus, растворилась бы в итоге документа. Отсутствие курса —
     * другое дело, оно штатно и обрабатывается внутри convertSync.
     *
     * @param from - валюта, в которой выражена эта сумма (объект или код)
     * @param to   - валюта, в которую конвертируем (объект или код)
     * @param date - дата курса
     */
    convertSync(from: Currency | string, to: Currency | string, date: Date | DateSimple): Sum | null {
        const fromCurrency = MoneyUtils.resolveCurrency(from);
        const toCurrency   = MoneyUtils.resolveCurrency(to);

        if (fromCurrency === null || toCurrency === null) {
            Core.error(`Sum.convertSync: неизвестная валюта (${from} → ${to})`);
            return null;
        } else {
            return MoneyUtils.convertSync(this, fromCurrency, toCurrency, date);
        }
    }

    /**
     * Сумма, приведённая к системной валюте отображения, готовой строкой.
     *
     * Сам Sum валюту не несёт — валюта, в которой он выражен, приходит
     * параметром: объектом Currency или просто кодом. Целевая валюта не
     * параметр: она системная, одна на приложение, берётся из ядра.
     * Единицы измерения у суммы нет (в отличие от Price) — только валюта.
     *
     * Дата курса обязательна и передаётся явно. Умолчания у неё нет
     * сознательно: у каждого документа своя дата курса (тендер приводится к
     * дате начала, отгрузка к своей), и забытый параметр дал бы не заглушку,
     * а правдоподобное неверное число без всякого признака ошибки. Там, где
     * текущая дата верна по смыслу (прайсы, актуальные оферы), пишется
     * DateSimple.today() — это видно в месте вызова.
     *
     * Метод синхронный и предназначен только для вывода. Курс читается из
     * реактивного кеша, поэтому осмысленный результат он даёт лишь внутри
     * реактивного контекста (шаблон, computed): присвоенный в обычное поле,
     * он не пересчитается ни при появлении курса, ни при смене валюты.
     *
     * Логика конвертации — в MoneyUtils.convertSync (одна на фронт), здесь
     * только форматирование результата.
     *
     * @param from - валюта, в которой выражена эта сумма (объект или код)
     * @param date - дата курса
     */
    formatInDisplayCurrency(from: Currency | string, date: Date | DateSimple): string {
        // Пустое (не введённое) значение печатается пустой строкой независимо от курса
        if (this.isEmpty()) {
            return '';
        }

        const fromCurrency    = MoneyUtils.resolveCurrency(from);
        const displayCurrency = MoneyUtils.displayCurrency;

        if (fromCurrency === null || displayCurrency === null) {
            return '';
        }

        const fallbackMark = MoneyUtils.getRateFallbackMark(fromCurrency, displayCurrency, date);

        if (fallbackMark !== null) {
            return fallbackMark;
        } else {
            return MoneyUtils.convertSync(this, fromCurrency, displayCurrency, date).format(displayCurrency);
        }
    }

}

export default Sum;